import { PRODUCTION_CONFIG } from '../config/production';

const SDK_URL = 'https://sdk.cashfree.com/js/v3/cashfree.js';

let loadPromise = null;
const cashfreeInstances = {};

function loadSdk() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Cashfree SDK requires a browser'));
  }
  if (window.Cashfree) return Promise.resolve(window.Cashfree);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SDK_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Cashfree));
      existing.addEventListener('error', () => reject(new Error('Failed to load Cashfree SDK')));
      if (window.Cashfree) resolve(window.Cashfree);
      return;
    }
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => resolve(window.Cashfree);
    script.onerror = () => reject(new Error('Failed to load Cashfree SDK'));
    document.head.appendChild(script);
  });

  return loadPromise;
}

const buildMode = () => (PRODUCTION_CONFIG.CASHFREE_MODE === 'production' ? 'production' : 'sandbox');

/**
 * One SDK instance per mode. The mode must match the Cashfree API that created the payment session, so the
 * server sends it with each order; the build-time setting is only a fallback for older servers.
 */
export async function getCashfree(mode = buildMode()) {
  const resolved = mode === 'production' ? 'production' : 'sandbox';
  if (cashfreeInstances[resolved]) return cashfreeInstances[resolved];
  const Cashfree = await loadSdk();
  cashfreeInstances[resolved] = Cashfree({ mode: resolved });
  return cashfreeInstances[resolved];
}

/**
 * Drop-in checkout. Supports all methods enabled on the merchant (cards, UPI, NB, wallets, EMI, paylater).
 * Caller must backend-verify order status after paymentDetails — never fulfil from client alone.
 *
 * @returns {'error'|'redirect'|'paymentDetails'}
 */
export async function openCashfreeCheckout(paymentSessionId, mode) {
  const cashfree = await getCashfree(mode || buildMode());
  const result = await cashfree.checkout({
    paymentSessionId,
    redirectTarget: '_modal',
  });

  if (result.error) return { state: 'error', result };
  if (result.redirect) return { state: 'redirect', result };
  if (result.paymentDetails) return { state: 'paymentDetails', result };
  return { state: 'error', result };
}

export const PAYMENT_COLLECTION_MODES = {
  OFFLINE: 'OFFLINE',
  ONLINE: 'ONLINE',
};

export const FEE_TYPES = {
  NONE: 'NONE',
  FLAT: 'FLAT',
  PERCENT: 'PERCENT',
  PERCENT_PLUS_FLAT: 'PERCENT_PLUS_FLAT',
};

export const FEE_PAYERS = { ATTENDEE: 'ATTENDEE', ORGANIZER: 'ORGANIZER' };

/** A Cashfree Easy Split vendor can receive settlements only once ACTIVE. */
export function isMerchantReady(merchant) {
  return !!merchant && merchant.enabled !== false && (merchant.cashfreeStatus || '').toUpperCase() === 'ACTIVE';
}

export const PAYMENT_PROVIDERS = { CASHFREE: 'CASHFREE', PAYU: 'PAYU' };
export const PROVIDER_LABELS = { CASHFREE: 'Cashfree', PAYU: 'PayU' };

/** SPLIT: the gateway splits each payment to the merchant. COLLECT: platform collects, admin transfers later. */
export const SETTLEMENT_MODES = { SPLIT: 'SPLIT', COLLECT: 'COLLECT' };

/** Can the chosen gateway pay this merchant directly (split at payment / API transfer)? */
export function isMerchantReadyFor(merchant, provider) {
  if (provider === PAYMENT_PROVIDERS.PAYU) {
    return !!merchant && merchant.enabled !== false && !!(merchant.payuChildKey || '').trim();
  }
  return isMerchantReady(merchant);
}

/** Posts the signed PayU form; the browser leaves the page for PayU's hosted checkout. */
export function submitPayuForm({ action, fields }) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  Object.entries(fields || {}).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value ?? '';
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/** Mirrors the backend PlatformFees; the server's figure is what is charged. */
export function platformFee(config, base) {
  if (!config || !(base > 0)) return 0;
  const percent = Number(config.feePercent) || 0;
  const flat = Number(config.feeFlat) || 0;
  let fee = 0;
  switch (config.feeType) {
    case FEE_TYPES.FLAT: fee = flat; break;
    case FEE_TYPES.PERCENT: fee = (base * percent) / 100; break;
    case FEE_TYPES.PERCENT_PLUS_FLAT: fee = (base * percent) / 100 + flat; break;
    default: fee = 0;
  }
  if (Number(config.feeCap) > 0) fee = Math.min(fee, Number(config.feeCap));
  return round2(Math.max(0, fee));
}

export function feeBreakdown(config, base) {
  const fee = platformFee(config, base);
  const payer = config?.feePayer || FEE_PAYERS.ATTENDEE;
  return {
    base: round2(base || 0),
    fee,
    payer,
    charged: payer === FEE_PAYERS.ATTENDEE ? round2((base || 0) + fee) : round2(base || 0),
    merchantShare: payer === FEE_PAYERS.ORGANIZER ? round2((base || 0) - fee) : round2(base || 0),
  };
}
